import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CheckCircle, Calendar, TrendingUp, Clock, MapPin, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from '../hooks/use-toast';

const ThankYouPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const carData = location.state?.carData;
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [bookingData, setBookingData] = useState({
    date: '',
    time: '',
    location: 'Viking Kontroll - Oslo',
    phone: ''
  });

  useEffect(() => {
    // If no car data, redirect to home
    if (!carData) {
      navigate('/');
    }
  }, [carData, navigate]);

  const handleBookTest = () => {
    setShowBookingForm(true);
  };

  const handleBookingSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!bookingData.date || !bookingData.time) {
      toast({
        title: "Fyll ut alle felt",
        description: "Velg dato og tid for testen",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Test booket!",
      description: "Du vil motta bekreftelse på e-post"
    });

    // Navigate to Min side after booking
    setTimeout(() => {
      navigate('/min-side');
    }, 2000);
  };

  if (!carData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 sm:py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, type: "spring" }}
          className="flex justify-center mb-6 sm:mb-8"
        >
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-green-50 flex items-center justify-center">
            <CheckCircle className="w-12 h-12 sm:w-14 sm:h-14 text-green-600" />
          </div>
        </motion.div>

        {/* Main Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-8 sm:mb-12"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Bilen er registrert!
          </h1>
          <p className="text-lg sm:text-xl text-gray-600">
            Nå må du booke en gratis test for å starte budrunden
          </p>
        </motion.div>

        {/* Car Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white border border-gray-200 rounded-2xl p-8 mb-8"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Din bil</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Bilmerke og modell:</span>
              <span className="font-medium text-gray-900">
                {carData.brand} {carData.model} ({carData.year})
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Reg.nr.:</span>
              <span className="font-medium text-gray-900">{carData.regNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Kilometer:</span>
              <span className="font-medium text-gray-900">
                {parseInt(carData.kilometers).toLocaleString('nb-NO')} km
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Drivstoff:</span>
              <span className="font-medium text-gray-900">{carData.fuelType}</span>
            </div>
            {carData.estimatedPrice && (
              <div className="flex justify-between pt-3 border-t border-gray-200">
                <span className="text-gray-600">Estimert verdi:</span>
                <span className="font-bold text-gray-900 text-lg">
                  {carData.estimatedPrice.toLocaleString('nb-NO')} kr
                </span>
              </div>
            )}
          </div>
        </motion.div>

        {!showBookingForm ? (
          /* Book Test CTA */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white border border-gray-200 rounded-2xl p-8 mb-8"
          >
            <div className="text-center mb-6">
              <Calendar className="w-12 h-12 text-gray-900 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Book gratis biltest
              </h2>
              <p className="text-gray-600">
                Før budrunden starter må bilen testes hos Viking Kontroll eller NAF. 
                Vi tar oss av kostnadene!
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3 text-sm text-gray-600">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Gratis profesjonell test hos Viking Kontroll eller NAF</span>
              </div>
              <div className="flex items-start gap-3 text-sm text-gray-600">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Tar 30-45 minutter</span>
              </div>
              <div className="flex items-start gap-3 text-sm text-gray-600">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <span>Budrunden starter umiddelbart etter testen</span>
              </div>
            </div>

            <Button 
              onClick={handleBookTest}
              className="w-full h-14 bg-gray-900 hover:bg-gray-800 text-white text-lg font-semibold"
            >
              Book test nå
            </Button>

            <button
              onClick={() => navigate('/min-side')}
              className="w-full mt-4 text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              Book senere fra Min side
            </button>
          </motion.div>
        ) : (
          /* Booking Form */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-gray-200 rounded-2xl p-8 mb-8"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Velg tidspunkt for biltest
            </h2>

            <form onSubmit={handleBookingSubmit} className="space-y-6">
              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  Teststed
                </label>
                <select
                  value={bookingData.location}
                  onChange={(e) => setBookingData({...bookingData, location: e.target.value})}
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                >
                  <option>Viking Kontroll - Oslo</option>
                  <option>Viking Kontroll - Bergen</option>
                  <option>Viking Kontroll - Trondheim</option>
                  <option>NAF - Oslo</option>
                  <option>NAF - Bergen</option>
                  <option>NAF - Stavanger</option>
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Dato
                </label>
                <Input
                  type="date"
                  value={bookingData.date}
                  onChange={(e) => setBookingData({...bookingData, date: e.target.value})}
                  min={new Date().toISOString().split('T')[0]}
                  className="h-12"
                  required
                />
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Tidspunkt
                </label>
                <select
                  value={bookingData.time}
                  onChange={(e) => setBookingData({...bookingData, time: e.target.value})}
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  required
                >
                  <option value="">Velg tidspunkt</option>
                  <option value="09:00">09:00</option>
                  <option value="10:00">10:00</option>
                  <option value="11:00">11:00</option>
                  <option value="12:00">12:00</option>
                  <option value="13:00">13:00</option>
                  <option value="14:00">14:00</option>
                  <option value="15:00">15:00</option>
                  <option value="16:00">16:00</option>
                </select>
              </div>

              {/* Phone (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-1" />
                  Telefon (valgfritt)
                </label>
                <Input
                  type="tel"
                  placeholder="12345678"
                  value={bookingData.phone}
                  onChange={(e) => setBookingData({...bookingData, phone: e.target.value})}
                  className="h-12"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Vi sender SMS-påminnelse hvis du oppgir telefonnummer
                </p>
              </div>

              <div className="flex gap-4">
                <Button 
                  type="submit"
                  className="flex-1 h-12 bg-gray-900 hover:bg-gray-800 text-white"
                >
                  Bekreft booking
                </Button>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setShowBookingForm(false)}
                  className="flex-1 h-12"
                >
                  Tilbake
                </Button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center mt-8 text-sm text-gray-500"
        >
          <p className="mb-2">Spørsmål? Vi hjelper deg gjerne!</p>
          <p className="font-medium text-gray-900">
            📞 47 49 49 00 · ✉️ hei@digicar.no
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ThankYouPage;
